import { computed } from "vue";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";

/**
 * Composable that computes summary data for the revision results page.
 *
 * @param {string} childId
 * @param {import('vue').ComputedRef} topicBreakdown - { [sgId]: { correct, total } }
 * @param {string} studentId - for building learn URLs
 */
export function useRevisionSummary(childId, topicBreakdown, studentId) {
  const progressStore = useProgressStore();
  const curriculumStore = useCurriculumStore();

  /**
   * Look up lesson info for a section group ID
   */
  function findLessonForSG(sgId) {
    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          for (const sub of lesson.subsections || []) {
            if (sub.content === sgId || sub.name === sgId) {
              return {
                lessonId: lesson.id,
                lessonTitle: lesson.title,
                subtopicTitle: sub.title || sgId
              };
            }
          }
        }
      }
    }
    return null;
  }

  function sgShortTitle(sgId) {
    const info = findLessonForSG(sgId);
    return info?.subtopicTitle || (sgId || "").replace(/^WS_/, "").replace(/_/g, " ");
  }

  function learnUrl(sgId) {
    const info = findLessonForSG(sgId);
    if (!info) return null;
    return `/student/${studentId}/learn/${info.lessonId}/${sgId}`;
  }

  /**
   * Enrich a topic entry with metadata
   */
  function enrichTopic(sgId, sessionData) {
    const childProgress = progressStore.getChildProgress(childId);
    const sgProgress = childProgress?.[sgId];
    const overallAccuracy = sgProgress?.accuracy ?? 0;
    const band = progressStore.getSectionGroupBand(childId, sgId);

    return {
      sgId,
      title: sgShortTitle(sgId),
      sessionCorrect: sessionData.correct,
      sessionTotal: sessionData.total,
      sessionPercent: sessionData.total > 0 ? Math.round((sessionData.correct / sessionData.total) * 100) : 0,
      overallAccuracy: Math.round(overallAccuracy * 100),
      band,
      learnUrl: learnUrl(sgId)
    };
  }

  /**
   * Top 3 strongest topics from this session
   */
  const strongestTopics = computed(() => {
    const bd = topicBreakdown.value;
    return Object.entries(bd)
      .map(([sgId, data]) => enrichTopic(sgId, data))
      .filter(t => t.sessionTotal > 0 && t.sessionPercent >= 60)
      .sort((a, b) => {
        if (b.sessionPercent !== a.sessionPercent) return b.sessionPercent - a.sessionPercent;
        return b.overallAccuracy - a.overallAccuracy;
      })
      .slice(0, 3);
  });

  /**
   * Top 3 weakest topics from this session (below 60%)
   */
  const weakestTopics = computed(() => {
    const bd = topicBreakdown.value;
    return Object.entries(bd)
      .map(([sgId, data]) => enrichTopic(sgId, data))
      .filter(t => t.sessionTotal > 0 && t.sessionPercent < 60)
      .sort((a, b) => {
        if (a.sessionPercent !== b.sessionPercent) return a.sessionPercent - b.sessionPercent;
        return a.overallAccuracy - b.overallAccuracy;
      })
      .slice(0, 3);
  });

  /**
   * Progress trend from revision history
   */
  const progressTrend = computed(() => {
    const history = progressStore.getRevisionHistory(childId);
    const last5 = history.slice(0, 5);
    const scores = last5.map(h => h.score);

    // Previous average (excluding the most recent which is the current session)
    const previous = scores.slice(1);
    const previousAverage = previous.length > 0
      ? Math.round(previous.reduce((a, b) => a + b, 0) / previous.length)
      : null;

    const currentScore = scores[0] ?? null;

    let direction = "same";
    if (previousAverage !== null && currentScore !== null) {
      if (currentScore > previousAverage) direction = "up";
      else if (currentScore < previousAverage) direction = "down";
    }

    return {
      previousAverage,
      currentScore,
      direction,
      history: scores
    };
  });

  /**
   * Overall revision stats
   */
  const overallStats = computed(() => {
    const history = progressStore.getRevisionHistory(childId);
    const totalRevisions = history.length;
    const scores = history.map(h => h.score);
    const averageScore = scores.length > 0
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : 0;
    const bestScore = scores.length > 0 ? Math.max(...scores) : 0;

    return { totalRevisions, averageScore, bestScore };
  });

  /**
   * All topics enriched (for the full topic breakdown section)
   */
  const allTopicsEnriched = computed(() => {
    const bd = topicBreakdown.value;
    return Object.entries(bd)
      .map(([sgId, data]) => enrichTopic(sgId, data))
      .sort((a, b) => a.sessionPercent - b.sessionPercent);
  });

  return {
    strongestTopics,
    weakestTopics,
    progressTrend,
    overallStats,
    allTopicsEnriched,
    findLessonForSG,
    sgShortTitle,
    learnUrl
  };
}
